import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { Button } from '@/components/chariot-ui';
import { XIcon } from 'lucide-react';
import { useState } from 'react';

import { Input } from '@/components/form/Input';
import { useSaveQuery } from '@/sections/insights/queryBuilder/hooks/useSaveQuery';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { generateUuid } from '@/utils/uuid.util';

export interface NewQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalQuery?: SavedQuery;
}

/**
 * Modal for creating new queries or duplicating existing queries
 *
 * Modes:
 * - 'new': Creates blank query with empty name/description
 * - 'duplicate': Pre-fills name with "(Duplicate)" suffix and copies description
 *
 * Generates UUID for new query ID (independent of name)
 * Forces folderId to "custom-queries"
 */
export const NewQueryModal = ({
  isOpen,
  onClose,
  originalQuery,
}: NewQueryModalProps) => {
  const [query, setQuery] = useState<SavedQuery>(
    originalQuery || {
      id: generateUuid(),
      name: '',
      description: '',
      type: 'table',
      folderId: 'custom-queries',
      query: {
        node: {
          labels: [],
          filters: [],
          relationships: [],
        },
      },
    }
  );

  const { setMode, setQuery: setGlobalQuery } = useQueryBuilder();

  const { save, isSaving } = useSaveQuery({
    onSuccess: onClose,
  });

  const handleSave = async () => {
    await save(query);

    // Only set query/mode for new queries
    if (!originalQuery) {
      setGlobalQuery(query);
      setMode('split-edit');
    }
  };

  const isNameValid = query.name.trim().length > 0;
  const modalTitle = originalQuery ? 'Duplicate Query' : 'New Query';

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-chariot-background-primary border-chariot-stroke mx-auto w-full max-w-3xl rounded-lg border p-6">
          <div className="mb-4 flex items-center justify-between">
            <DialogTitle className="text-chariot-text-primary text-lg font-semibold">
              {modalTitle}
            </DialogTitle>
            <button
              onClick={onClose}
              className="text-chariot-text-secondary hover:text-chariot-text-primary"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="query-name"
                className="text-chariot-text-primary mb-1 block text-sm font-medium"
              >
                Name
              </label>
              <Input
                name="query-name"
                value={query.name}
                onChange={e => setQuery({ ...query, name: e.target.value })}
                placeholder="Enter query name"
              />
            </div>

            <div>
              <label
                htmlFor="query-description"
                className="text-chariot-text-primary mb-1 block text-sm font-medium"
              >
                Description
              </label>
              <textarea
                id="query-description"
                value={query.description}
                onChange={e =>
                  setQuery({ ...query, description: e.target.value })
                }
                placeholder="Enter query description"
                rows={6}
                className="bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary placeholder:text-chariot-text-tertiary focus:ring-chariot-brand-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button
              styleType="secondary"
              onClick={onClose}
              label="Cancel"
              disabled={isSaving}
            />
            <Button
              styleType="primary"
              onClick={handleSave}
              label={isSaving ? 'Saving...' : 'Save'}
              disabled={!isNameValid || isSaving}
            />
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
